import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { wordpressAPI } from '../services/wordpressApi';
import { WPPage } from '../types/wordpress';
import ResourceContent from '../components/resources/ResourceContent';
import Breadcrumb from '../components/layout/Breadcrumb';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import { decodeHtmlEntities } from '../utils/htmlEntities';
import { usePageTitle } from '../hooks/usePageTitle';

/** Routes whose public path differs from the WordPress slug. */
const STATIC_PAGE_SLUGS: Record<string, string> = {
  '/privacy': 'privacy',
  '/terms': 'terms-of-use',
  '/useful-links': 'useful-links',
};

export default function GenericPage() {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const slug = STATIC_PAGE_SLUGS[location.pathname] ?? paramSlug;
  const [page, setPage] = useState<WPPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle(page ? decodeHtmlEntities(page.title.rendered) : undefined);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) {
        setError(new Error('Invalid page slug'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const pageData = await wordpressAPI.fetchPageBySlug(slug);
        if (!pageData) {
          setError(new Error('Page not found'));
        } else {
          setPage(pageData);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load page'));
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState
          error={error || new Error('Page not found')}
          title="Page Not Found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: decodeHtmlEntities(page.title.rendered) },
        ]}
      />

      <article>
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {decodeHtmlEntities(page.title.rendered)}
          </h1>
        </header>

        <ResourceContent content={page.content.rendered} />
      </article>
    </div>
  );
}

